import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Inbox, Loader2 } from "lucide-react";
import { EscBadge, EscHistory } from "@/components/admin/TeamRouting";
import { TEAMS } from "@/lib/teams";
import { fmtTs } from "@/components/Timeline";

const NEXT = { Assigned: ["Accepted", "In progress", "Resolved", "Sent back"], Accepted: ["In progress", "Resolved", "Sent back"], "In progress": ["Resolved", "Sent back"], Resolved: [], "Sent back": [] };

function Task({ e, onDone, canBook }) {
    const [note, setNote] = useState("");
    const [open, setOpen] = useState(false);
    const act = async (status) => {
        try { await api.patch(`/admin/escalations/${e.id}`, { status, note }); toast.success(`Marked ${status.toLowerCase()}`); setNote(""); onDone(); }
        catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    };
    return (
        <div data-testid={`task-${e.ref}`} className="rounded-2xl border border-navy/10 bg-white">
            <button onClick={() => setOpen(!open)} className="flex w-full flex-wrap items-center gap-2 p-4 text-left">
                <div className="min-w-0 flex-1">
                    <p className="font-heading font-bold text-navy">{e.ref} · {e.title}</p>
                    <p className="text-xs text-navy/55">{e.kind === "ticket" ? "Helpdesk ticket" : "Complaint"} · {e.raised_by}{e.booking_ref ? ` · ${e.booking_ref}` : ""} · sent by {e.created_by} · {fmtTs(e.updated_at)}</p>
                </div>
                <EscBadge e={e} />
            </button>
            {open && (
                <div className="space-y-3 border-t border-navy/10 p-4">
                    {e.detail && <p className="rounded-xl bg-navy/[0.03] p-3 text-sm text-navy/80">{e.detail}</p>}
                    <EscHistory e={e} />
                    {NEXT[e.status]?.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                            <input data-testid={`task-note-${e.ref}`} value={note} onChange={(x) => setNote(x.target.value)} placeholder="Update for Helpdesk (required to resolve / send back)" className="min-w-[200px] flex-1 rounded-full border border-navy/15 px-3 py-2 text-xs outline-none focus:border-orange" />
                            {NEXT[e.status].map((s) => <button key={s} data-testid={`task-${s.replace(/\s+/g, "-").toLowerCase()}-${e.ref}`} onClick={() => act(s)} className={`rounded-full px-3 py-2 text-xs font-bold ${s === "Resolved" ? "bg-emerald-600 text-white" : s === "Sent back" ? "border border-red-300 text-red-600" : "bg-navy/5 text-navy/70"}`}>{s}</button>)}
                        </div>
                    )}
                    {e.booking_id && canBook && <Link to={`/admin/bookings/${e.booking_id}`} className="inline-block rounded-full border border-navy/15 px-3 py-1.5 text-xs font-bold text-navy">Open booking</Link>}
                </div>
            )}
        </div>
    );
}

export default function AdminTeamInbox() {
    const { user } = useAuth();
    const helpdesk = !user?.is_staff || (user.sections || []).some((s) => ["support", "complaints"].includes(s));
    const [rows, setRows] = useState(null);
    const [team, setTeam] = useState("");
    const [st, setSt] = useState("open");
    const load = useCallback(() => {
        api.get(`/admin/escalations${team ? `?team=${team}` : ""}`).then((r) => setRows(r.data)).catch(() => setRows([]));
    }, [team]);
    useEffect(() => { load(); }, [load]);
    const shown = (rows || []).filter((e) => (st === "all" ? true : st === "open" ? !["Resolved", "Sent back"].includes(e.status) : ["Resolved", "Sent back"].includes(e.status)));
    return (
        <div>
            <h1 className="flex items-center gap-2 font-heading text-2xl font-extrabold text-navy"><Inbox className="text-orange" /> Team Inbox</h1>
            <p className="text-sm text-navy/55">{helpdesk ? "Everything Helpdesk has sent to a department and where it stands." : `Tickets and complaints sent to the ${TEAMS[user?.team] || ""} team.`}</p>
            <div className="mt-4 flex flex-wrap gap-2">
                {[["open", "Open"], ["done", "Resolved / sent back"], ["all", "All"]].map(([k, l]) => <button key={k} data-testid={`inbox-filter-${k}`} onClick={() => setSt(k)} className={`rounded-full px-3 py-1.5 text-xs font-bold ${st === k ? "bg-orange text-white" : "border border-navy/15 bg-white text-navy/60"}`}>{l}</button>)}
                {helpdesk && <select data-testid="inbox-team-filter" value={team} onChange={(e) => setTeam(e.target.value)} className="rounded-full border border-navy/15 bg-white px-3 py-1.5 text-xs font-bold text-navy"><option value="">All teams</option>{Object.entries(TEAMS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>}
            </div>
            {rows === null ? <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-orange" /></div> : (
                <div className="mt-4 space-y-3">
                    {shown.map((e) => <Task key={e.id} e={e} onDone={load} canBook={!user?.is_staff || (user.sections || []).includes("bookings")} />)}
                    {!shown.length && <div className="rounded-2xl border border-dashed border-navy/15 py-12 text-center text-sm text-navy/40">Nothing here.</div>}
                </div>
            )}
        </div>
    );
}
